import styled from "@emotion/styled";
import { TextField } from "react-aria-components";
import { MDSTextFieldVariant, MDSTextFieldProps } from "./text-field.types";
import { MDSIconButton } from "@/mds/components/buttons";
import { MdsIcon } from "@/design-system/components/icon";

export const StyledTextFieldContainer = styled.div`
  flex-shrink: 0;
`;

export const StyledTextField = styled(TextField)<
  MDSTextFieldProps & {
    $variant?: MDSTextFieldVariant;
    $hasError?: boolean;
    $isFocused?: boolean;
    $isTextArea?: boolean;
  }
>`
  display: flex;
  position: relative;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  width: 100%;
  min-height: 40px;
  padding: ${({ $isTextArea, theme }) =>
    $isTextArea
      ? `${theme.spacing["3"]} ${theme.spacing["5"]} ${theme.spacing["5"]}  ${theme.spacing["5"]}`
      : `${theme.spacing["2"]} ${theme.spacing["5"]}`};

  ${({ $variant = MDSTextFieldVariant.Default, $hasError, $isFocused, $isTextArea, theme }) => {
    switch ($variant) {
      case MDSTextFieldVariant.Form:
        return `
          border-radius: ${theme.borderRadius.large};
          border: 1px solid ${$hasError ? theme.colors.border.input.default.danger : $isFocused ? theme.colors.border.input.default.focused : theme.colors.border.input.default.default};
          background: ${$isFocused ? theme.colors.background.input.default.focused : theme.colors.background.input.default.default};
        `;

      case MDSTextFieldVariant.Brand:
        return `
          border-radius: ${$isTextArea ? theme.borderRadius.large : theme.borderRadius.xlarge};
          border: 1px solid ${$isFocused ? theme.colors.border.input.brand.focused : theme.colors.border.input.brand.default};
          background: ${$isFocused ? theme.colors.background.input.onAccent.focused : theme.colors.background.input.onAccent.default};
        `;

      case MDSTextFieldVariant.Emphasis:
        return `
          border-radius: ${$isTextArea ? theme.borderRadius.large : theme.borderRadius.xlarge};
          border: 1px solid ${$isFocused ? theme.colors.border.input.emphasis.focused : theme.colors.border.input.emphasis.default};
          background: ${$isFocused ? theme.colors.background.input.default.focused : theme.colors.background.input.default.default};
        `;

      case MDSTextFieldVariant.Chrome:
        return `
          border-radius: ${$isTextArea ? theme.borderRadius.large : theme.borderRadius.xlarge};
          border: 1px solid ${$isFocused ? theme.colors.background.input.chrome.focused : theme.colors.background.input.chrome.default};
          background: ${$isFocused ? theme.colors.background.input.chrome.focused : theme.colors.background.input.chrome.default};
        `;

      case MDSTextFieldVariant.Filter:
        return `
          border-radius: 0;
          border: none;
          background: transparent;
        `;

      default:
        return `
            border-radius: ${$isTextArea ? theme.borderRadius.large : theme.borderRadius.xlarge};
            border: 1px solid ${$hasError ? theme.colors.border.input.default.danger : $isFocused ? theme.colors.border.input.default.focused : theme.colors.border.input.default.default};
            background: ${$isFocused ? theme.colors.background.input.default.focused : theme.colors.background.input.default.default};
          `;
    }
  }}

  transition: all 0.2s ease;
  outline: none;

  &[data-disabled] {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &[data-invalid] {
    border-color: ${({ theme }) => theme.colors.border.input.default.danger};
  }

  input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: ${({ theme }) => theme.fontSizes.small};
    line-height: ${({ theme }) => theme.lineHeights.xsmall};
    outline: none;
    color: ${({ theme }) => theme.colors.text.primary};

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.tertiary};
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  textarea {
    flex: 1;
    border: none;
    background: transparent;
    font-size: ${({ theme }) => theme.fontSizes.small};
    line-height: ${({ theme }) => theme.lineHeights.xsmall};
    outline: none;
    resize: none;
    color: ${({ theme }) => theme.colors.text.primary};

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.tertiary};
    }

    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const StyledLeftContainer = styled.div<{
  $variant?: MDSTextFieldVariant;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledRightContainer = styled.div<{
  $variant?: MDSTextFieldVariant;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;

  ${({ $variant, theme }) => {
    if ($variant === MDSTextFieldVariant.Brand) {
      return `
      height: ${theme.spacing["8"]};
      width: ${theme.spacing["8"]};
      `;
    }
  }}
`;

export const BrandIcon = styled(MDSIconButton)`
  padding: 0;

  & .mds-btn-icon-wrapper {
    height: ${({ theme }) => theme.spacing["8"]};
    width: ${({ theme }) => theme.spacing["8"]};
  }

  & .mds-icon-wrapper {
    height: ${({ theme }) => theme.spacing["8"]};
    width: ${({ theme }) => theme.spacing["8"]};
  }

  & .mds-icon {
    ${({ theme }) => `
      height: ${theme.spacing["8"]};
      width: ${theme.spacing["8"]};
    `};
  }
`;

export const StyledError = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme }) => theme.spacing["1"]} 0;
`;

export const StyledErrorIcon = styled(MdsIcon)`
  color: ${({ theme }) => theme.colors.icon.danger};
`;
